import type {
  APIComponentAttributes,
  APIFieldAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { componentOperations } from '@ardoq/core';
import type {
  ContextShape,
  RepresentationData,
  WorkspaceAndModelLookup,
} from '@ardoq/data-model';
import { spreadMap } from '@ardoq/rxbeach';
import { combineLatest, type Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import {
  type ComponentsState,
  components$,
} from 'streams/components/components$';
import { context$ } from 'streams/context/context$';
import {
  type FieldsByWorkspaceId,
  fieldsByWorkspaceId$,
} from 'streams/fields/fieldsByWorkspaceId$';
import {
  type GroupingRulesState,
  groupingRules$,
} from 'streams/grouping/groupingRules$';
import { resolveComponentType } from 'streams/resolveComponentTypeFromWorkspaceAndModel';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';

export type RoadmapViewData = {
  context: ContextShape;
  hasGroupingRules: boolean;
  workspaceFields: readonly APIFieldAttributes[];
  componentsById: Partial<Record<ArdoqId, APIComponentAttributes>>;
  workspaceAndModel: WorkspaceAndModelLookup;
};

export const toRoadmapViewData = (
  context: ContextShape,
  { groupingRules }: GroupingRulesState,
  fieldsByWorkspaceId: FieldsByWorkspaceId,
  { byId }: ComponentsState,
  workspaceAndModel: WorkspaceAndModelLookup
): RoadmapViewData => ({
  context,
  hasGroupingRules: groupingRules.length > 0,
  workspaceFields: fieldsByWorkspaceId[context.workspaceId] ?? [],
  componentsById: byId,
  workspaceAndModel,
});

export const roadmapViewData$: Observable<RoadmapViewData> = combineLatest([
  context$,
  groupingRules$,
  fieldsByWorkspaceId$,
  components$,
  workspaceAndModel$,
]).pipe(
  spreadMap(toRoadmapViewData),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const getRepresentationData = (
  { componentsById, workspaceAndModel }: RoadmapViewData,
  componentId: ArdoqId
): RepresentationData | null => {
  const component = componentsById[componentId];
  if (!component) return null;
  return componentOperations.getRepresentationData(
    component,
    resolveComponentType(component, workspaceAndModel)
  );
};
